import { defineProp, emptyMap, emptySet } from './functionalUtils';

export {};

declare global {
  interface Array<T> {
    dropWhile(this: T[], predicate: (item: T) => boolean): T[];
    toMapBy<K>(this: T[], keyFunc: (item: T) => K): Map<K, T>;
    toSetBy<K>(this: T[], keyFunc: (item: T) => K): Set<K>;
    toMapByKeyValue<K, V>(this: T[], keyFunc: (item: T) => K, valueFunc: (item: T) => V): Map<K, V>;
    alsoForEach(this: T[], block: (item: T) => void): T[];
    median(this: number[]): number;
    min(this: number[]): number;
    max(this: number[]): number;
    sum(this: number[]): number;
    findFirst(this: T[], predicate: (item: T) => boolean, fallback: T): T;
    minBy(this: T[], valueFunc: (item: T) => number): number;
    maxBy(this: T[], valueFunc: (item: T) => number): number;
    sumBy(this: T[], valueFunc: (item: T) => number): number;
    findFirstBy<V>(this: T[], valueFunc: (item: T) => V, predicate: (item: V) => boolean, fallback: V): V;
    addAll(this: T[], ...items: T[]): T[];
  }
}

const _dropWhile = function <T>(this: T[], predicate: (item: T) => boolean): T[] {
  // We want to drop items while the predicate is true, so we find the first index
  // Where the predicate is false, and drop previous items
  const index = this.findIndex((item) => predicate(item) === false);

  return index === -1 ? [] : this.slice(index);
};

const _toMapBy = function <K, T>(this: T[], keyFunc: (item: T) => K): Map<K, T> {
  return this.reduce((map, item) => map.set(keyFunc(item), item), emptyMap<K, T>());
};

const _toSetBy = function <K, T>(this: T[], keyFunc: (item: T) => K): Set<K> {
  return this.reduce((set, item) => set.add(keyFunc(item)), emptySet<K>());
};

const _toMapByKeyValue = function <T, K, V>(this: T[], keyFunc: (item: T) => K, valueFunc: (item: T) => V): Map<K, V> {
  return this.reduce((map, item) => map.set(keyFunc(item), valueFunc(item)), emptyMap<K, V>());
};

const _alsoForEach = function <T>(this: T[], block: (item: T) => void): T[] {
  this.forEach((item: T) => block(item));
  return this;
};

const _min = function (this: number[]): number {
  return this.reduce((min, item) => Math.min(min, item), Number.MAX_VALUE);
};

const _max = function (this: number[]): number {
  return this.reduce((max, item) => Math.max(max, item), Number.MIN_VALUE);
};

const _sum = function (this: number[]): number {
  return this.reduce((sum, item) => sum + item, 0);
};

const _findFirst = function <T>(this: T[], predicate: (item: T) => boolean, fallback: T): T {
  for (const item of this) if (predicate(item)) return item;

  return fallback;
};

const _minBy = function <T>(this: T[], valueFunc: (item: T) => number): number {
  return this.reduce((min, item) => Math.min(min, valueFunc(item)), Number.MAX_VALUE);
};

const _maxBy = function <T>(this: T[], valueFunc: (item: T) => number): number {
  return this.reduce((max, item) => Math.max(max, valueFunc(item)), Number.MIN_VALUE);
};

const _sumBy = function <T>(this: T[], valueFunc: (item: T) => number): number {
  return this.reduce((sum, item) => sum + valueFunc(item), 0);
};

const _findFirstBy = function <T, V>(
  this: T[],
  valueFunc: (item: T) => V,
  predicate: (item: V) => boolean,
  fallback: V
): V {
  for (const item of this) {
    const value = valueFunc(item);
    if (predicate(value)) return value;
  }

  return fallback;
};

const _addAll = function <T>(this: T[], ...items: T[]): T[] {
  this.push(...items);
  return this;
};

const _median = function (this: number[]): number {
  const sorted = this.sort();
  const isOdd = sorted.length % 2 === 1;
  const mid = Math.trunc(sorted.length / 2);
  return isOdd ? sorted[mid + 1] : (sorted[mid - 1] + sorted[mid]) / 2;
};

if (!Array.prototype.dropWhile) {
  defineProp(Array.prototype, 'dropWhile', _dropWhile);
  defineProp(Array.prototype, 'toMapBy', _toMapBy);
  defineProp(Array.prototype, 'toMapByKeyValue', _toMapByKeyValue);
  defineProp(Array.prototype, 'toSetBy', _toSetBy);
  defineProp(Array.prototype, 'alsoForEach', _alsoForEach);
  defineProp(Array.prototype, 'median', _median);
  defineProp(Array.prototype, 'min', _min);
  defineProp(Array.prototype, 'max', _max);
  defineProp(Array.prototype, 'sum', _sum);
  defineProp(Array.prototype, 'findFirst', _findFirst);
  defineProp(Array.prototype, 'minBy', _minBy);
  defineProp(Array.prototype, 'maxBy', _maxBy);
  defineProp(Array.prototype, 'sumBy', _sumBy);
  defineProp(Array.prototype, 'findFirstBy', _findFirstBy);
  defineProp(Array.prototype, 'addAll', _addAll);
}
